import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useParams, Link } from "react-router-dom";
import { fetchEventDetail, fetchEventDetailStock, EventDetail, PrizeWithAllocations, type ImageVariants } from "@/services/customerService";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGacha } from "@/hooks/useGacha";
import { useAuth } from "@/components/AuthContext";
import LotteryAnimation from "@/components/LotteryAnimation";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";
import { calculateProgress, getProgressColor, getProgressLabel, isSoldOut } from "@/utils/progressUtils";
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import ResponsiveImage from "@/components/ResponsiveImage";
import { ThemedButton } from "@/components/themed";

interface EventDetailsPageProps {
  siteId: number;
}

type ModalImage = {
  key: string;
  url: string | null;
  variants?: ImageVariants;
  alt: string;
  mode: "custom_thumbnails" | "products";
};

const buildPrizeModalImages = (prize: PrizeWithAllocations, prizeKey: string): ModalImage[] => {
  const displayThumbnailUrls = prize.display_thumbnail_urls || [];
  const displayThumbnailVariants = prize.display_thumbnail_variant_urls || [];
  const hasDisplayThumbnails =
    prize.display_thumbnail_mode === "custom_thumbnails" && displayThumbnailUrls.length > 0;

  if (hasDisplayThumbnails) {
    return displayThumbnailUrls.map((url, index) => ({
      key: `custom-${prizeKey}-${index}`,
      url,
      variants: displayThumbnailVariants[index],
      alt: `${prize.name} 掲載デザイン ${index + 1}`,
      mode: "custom_thumbnails",
    }));
  }

  return prize.allocations.map((allocation) => ({
    key: `allocation-${allocation.id}`,
    url: allocation.image_url,
    variants: allocation.image_variant_urls,
    alt: allocation.product_name || "商品",
    mode: "products",
  }));
};

const getNextDailyResetAt = (base = new Date()) => {
  const resetAt = new Date(base);
  resetAt.setDate(resetAt.getDate() + 1);
  resetAt.setHours(0, 0, 0, 0);
  return resetAt;
};

const EventDetailsPage: React.FC<EventDetailsPageProps> = ({ siteId }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { currentUser } = useAuth();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dailyCountdown, setDailyCountdown] = useState('');

  // Use event's gacha_cost or default to 400
  const gachaCost = event?.gacha_cost || 400;

  // Calculate progress metrics (新旧両方式対応の統合口数を使用)
  const totalStock = event?.total_stock_count ?? event?.lottery_pools_count ?? 0;
  const rawAvailableStock = event?.available_stock_count ?? event?.lottery_pools_available_count;
  const hideRemainingStock = rawAvailableStock == null && event?.status === 'finished';
  const availableStock = rawAvailableStock ?? 0;
  const progressPercentage = event ? calculateProgress(availableStock, totalStock) : 0;
  const progressColor = event ? getProgressColor(progressPercentage, availableStock) : 'bg-gray-400';
  const progressLabel = event ? getProgressLabel(availableStock, totalStock) : '';
  const soldOut = event ? isSoldOut(availableStock) : false;
  // 開催中かどうか
  const isActive = event?.status === 'active';
  const isLegacyEvent = event?.use_product_stock_lottery === false;
  const dailyPlayLimit = event?.daily_play_limit;
  const dailyPlaysRemaining = event?.daily_plays_remaining;
  const isDailyLimitEvent = dailyPlayLimit != null && dailyPlayLimit > 0;
  const dailyLimitReached = isDailyLimitEvent && dailyPlaysRemaining != null && dailyPlaysRemaining <= 0;
  const totalPlayLimit = event?.total_play_limit;
  const totalPlaysRemaining = event?.total_plays_remaining;
  const isTotalLimitEvent = totalPlayLimit != null && totalPlayLimit > 0;
  const totalLimitReached = isTotalLimitEvent && totalPlaysRemaining != null && totalPlaysRemaining <= 0;
  const isNewCustomerOnlyEvent = event?.new_customer_only_days != null && event.new_customer_only_days > 0;
  const newCustomerEligible = currentUser == null || event?.new_customer_eligible !== false;
  const newCustomerDaysRemaining = event?.new_customer_days_remaining;
  const newCustomerExpiresAt = event?.new_customer_expires_at;
  const showNewCustomerDaysRemaining = isNewCustomerOnlyEvent && newCustomerEligible && newCustomerDaysRemaining != null;
  const newCustomerExpiresAtLabel = newCustomerExpiresAt
    ? new Date(newCustomerExpiresAt).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;
  const newCustomerExpiresAtDate = newCustomerExpiresAt ? new Date(newCustomerExpiresAt) : null;
  const nextDailyResetAt = dailyLimitReached ? getNextDailyResetAt() : null;
  const newCustomerExpiresBeforeDailyReset =
    dailyLimitReached &&
    newCustomerExpiresAtDate != null &&
    nextDailyResetAt != null &&
    newCustomerExpiresAtDate.getTime() < nextDailyResetAt.getTime();
  const paidAmountRequirementEnabled =
    event?.paid_amount_requirement_scope != null &&
    event?.paid_amount_requirement_minimum != null &&
    event.paid_amount_requirement_minimum > 0;
  const paidAmountRequirementBlocked =
    paidAmountRequirementEnabled && event?.paid_amount_requirement_eligible === false;
  const customerListRestricted = event?.customer_list_restricted === true;
  const customerListBlocked = customerListRestricted && event?.customer_list_eligible === false;
  const customerListConditionText =
    event?.customer_list_condition_text || "このイベントは対象のお客様限定で参加できます。";
  const formatAmount = (amount?: number | null) => (amount ?? 0).toLocaleString('ja-JP');
  const paidAmountRequirementScopeLabel =
    event?.paid_amount_requirement_scope === 'monthly' ? '当月ポイントチャージ実績' : '累計ポイントチャージ実績';
  // 開催期間判定（未設定なら常時開催扱い）
  const startsAtDate = event?.starts_at ? new Date(event.starts_at) : null;
  const endsAtDate = event?.ends_at ? new Date(event.ends_at) : null;
  const nowDate = new Date();
  const isBeforeStart = startsAtDate !== null && nowDate < startsAtDate;
  const isAfterEnd = endsAtDate !== null && nowDate > endsAtDate;
  const isWithinPeriod = !isBeforeStart && !isAfterEnd;
  const canDraw = isActive && !soldOut && !isLegacyEvent && !dailyLimitReached && !totalLimitReached && newCustomerEligible && !paidAmountRequirementBlocked && !customerListBlocked && isWithinPeriod;
  const participationStatusKnown = currentUser != null &&
    (!paidAmountRequirementEnabled || event?.paid_amount_requirement_eligible != null) &&
    (!customerListRestricted || event?.customer_list_eligible != null);

  // ログボガチャ カウントダウンタイマー（0:00到達時にAPI再取得でボタン復活）
  useEffect(() => {
    if (!dailyLimitReached) return;
    const timer = setInterval(() => {
      const now = new Date();
      const tomorrow = getNextDailyResetAt(now);
      const diff = tomorrow.getTime() - now.getTime();
      if (diff <= 0) {
        // 0:00到達 → stock APIを再取得してボタン復活
        clearInterval(timer);
        setDailyCountdown('0:00:00');
        if (eventId) {
          fetchEventDetailStock(siteId, eventId).then((stockData) => {
            setEvent((prev) => prev ? {
              ...prev,
              status: stockData.status || prev.status,
              total_stock_count: stockData.total_stock_count ?? prev.total_stock_count,
              available_stock_count: stockData.available_stock_count ?? prev.available_stock_count,
              sold_stock_count: stockData.sold_stock_count ?? prev.sold_stock_count,
              lottery_pools_count: stockData.lottery_pools_count ?? prev.lottery_pools_count,
              lottery_pools_available_count: stockData.lottery_pools_available_count ?? prev.lottery_pools_available_count,
              daily_play_limit: stockData.daily_play_limit ?? prev.daily_play_limit,
              daily_plays_remaining: stockData.daily_plays_remaining,
              total_play_limit: stockData.total_play_limit ?? prev.total_play_limit,
              total_plays_remaining: stockData.total_plays_remaining,
              new_customer_only_days: stockData.new_customer_only_days ?? prev.new_customer_only_days,
              new_customer_eligible: stockData.new_customer_eligible,
              new_customer_days_remaining: stockData.new_customer_days_remaining,
              new_customer_expires_at: stockData.new_customer_expires_at,
              paid_amount_requirement_scope: stockData.paid_amount_requirement_scope ?? prev.paid_amount_requirement_scope,
              paid_amount_requirement_minimum: stockData.paid_amount_requirement_minimum ?? prev.paid_amount_requirement_minimum,
              paid_amount_requirement_label: stockData.paid_amount_requirement_label ?? prev.paid_amount_requirement_label,
              paid_amount_requirement_current_amount: stockData.paid_amount_requirement_current_amount,
              paid_amount_requirement_eligible: stockData.paid_amount_requirement_eligible,
              customer_list_restricted: stockData.customer_list_restricted ?? prev.customer_list_restricted,
              customer_list_eligible: stockData.customer_list_eligible,
              customer_list_condition_text: stockData.customer_list_condition_text ?? prev.customer_list_condition_text,
            } : prev);
          }).catch(() => {});
        }
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setDailyCountdown(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(timer);
  }, [dailyLimitReached, siteId, eventId]);

  useEffect(() => {
    const loadEventDetail = async () => {
      try {
        if (!eventId) throw new Error("Missing eventId");
        // 静的情報と在庫情報を並行取得
        const [eventData, stockData] = await Promise.all([
          fetchEventDetail(siteId, eventId),
          fetchEventDetailStock(siteId, eventId),
        ]);
        // 在庫情報をマージ
        setEvent({
          ...eventData,
          // 在庫APIのstatusを優先（キャッシュが短いため新鮮）
          status: stockData.status || eventData.status,
          total_stock_count: stockData.total_stock_count ?? eventData.total_stock_count,
          available_stock_count: stockData.available_stock_count ?? eventData.available_stock_count,
          sold_stock_count: stockData.sold_stock_count ?? eventData.sold_stock_count,
          lottery_pools_count: stockData.lottery_pools_count ?? eventData.lottery_pools_count,
          lottery_pools_available_count: stockData.lottery_pools_available_count ?? eventData.lottery_pools_available_count,
          // ログボガチャ（1日回数制限）
          daily_play_limit: stockData.daily_play_limit ?? eventData.daily_play_limit,
          daily_plays_remaining: stockData.daily_plays_remaining,
          total_play_limit: stockData.total_play_limit ?? eventData.total_play_limit,
          total_plays_remaining: stockData.total_plays_remaining,
          new_customer_only_days: stockData.new_customer_only_days ?? eventData.new_customer_only_days,
          new_customer_eligible: stockData.new_customer_eligible,
          new_customer_days_remaining: stockData.new_customer_days_remaining,
          new_customer_expires_at: stockData.new_customer_expires_at,
          // ポイントチャージ実績条件
          paid_amount_requirement_scope: stockData.paid_amount_requirement_scope ?? eventData.paid_amount_requirement_scope,
          paid_amount_requirement_minimum: stockData.paid_amount_requirement_minimum ?? eventData.paid_amount_requirement_minimum,
          paid_amount_requirement_label: stockData.paid_amount_requirement_label ?? eventData.paid_amount_requirement_label,
          paid_amount_requirement_current_amount: stockData.paid_amount_requirement_current_amount,
          paid_amount_requirement_eligible: stockData.paid_amount_requirement_eligible,
          // 顧客リスト限定
          customer_list_restricted: stockData.customer_list_restricted ?? eventData.customer_list_restricted,
          customer_list_eligible: stockData.customer_list_eligible,
          customer_list_condition_text: stockData.customer_list_condition_text ?? eventData.customer_list_condition_text,
        });
      } catch (err: any) {
        setError(
          err?.response?.status === 404
            ? "このイベントは現在ご利用いただけません"
            : "イベント情報を取得できませんでした"
        );
      } finally {
        setLoading(false);
      }
    };

    loadEventDetail();
  }, [siteId, eventId]);

  const {
    handleGachaClick,
    executeGacha,
    onAnimationEnd,
    skipAllAnimations,
    isConfirmDialogOpen,
    isLoginDialogOpen,
    isPointDialogOpen,
    isAnimationPlaying,
    setIsConfirmDialogOpen,
    setIsLoginDialogOpen,
    setIsPointDialogOpen,
    pendingGacha,
    currentAnimationUrl,
    currentAnimationIndex,
    drawResults,
    isPhoneVerificationRequired,
    setIsPhoneVerificationRequired,
  } = useGacha();

  // 抽選成功後にstock再取得（drawResultsが変化 = 抽選実行完了）
  useEffect(() => {
    if (!eventId || (!isDailyLimitEvent && !isTotalLimitEvent) || drawResults.length === 0) return;
    fetchEventDetailStock(siteId, eventId).then((stockData) => {
      setEvent((prev) => prev ? {
        ...prev,
        daily_plays_remaining: stockData.daily_plays_remaining,
        total_plays_remaining: stockData.total_plays_remaining,
        paid_amount_requirement_current_amount: stockData.paid_amount_requirement_current_amount,
        paid_amount_requirement_eligible: stockData.paid_amount_requirement_eligible,
        customer_list_restricted: stockData.customer_list_restricted ?? prev.customer_list_restricted,
        customer_list_eligible: stockData.customer_list_eligible,
        customer_list_condition_text: stockData.customer_list_condition_text ?? prev.customer_list_condition_text,
      } : prev);
    }).catch(() => {});
  }, [drawResults, siteId, eventId, isDailyLimitEvent, isTotalLimitEvent]);

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-theme-bg"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div></div>;
  if (error) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-theme-bg px-4 text-center">
      <p className="text-red-600 [font-weight:var(--theme-font-weight-button)]">{error}</p>
      <Link to="/" className="underline [font-weight:var(--theme-font-weight-button)]">ホームに戻る</Link>
    </div>
  );
  if (!event) return <div className="flex items-center justify-center min-h-screen bg-theme-bg"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div></div>;

  const allPrizeModalImages = event.prizes_with_allocations?.flatMap((prize, prizeIndex) =>
    prize.allocations.length > 0
      ? buildPrizeModalImages(prize, String(prize.id || `unassigned-${prizeIndex}`))
      : []
  ) ?? [];
  const customPrizeModalImages = allPrizeModalImages.filter((image) => image.mode === "custom_thumbnails");
  const productPrizeModalImages = allPrizeModalImages.filter((image) => image.mode === "products");

  return (
    <>
      <Helmet>
        <title>{event?.site?.name ? `${event.name || 'オリパ詳細'} | ${event.site.name}` : (event?.name || 'オリパ詳細')}</title>
        {/* OGP Tags */}
        <meta property="og:title" content={event?.site?.name ? `${event.name || 'オリパ詳細'} | ${event.site.name}` : (event?.name || 'オリパ詳細')} />
        <meta property="og:description" content={event?.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 200) : `${event?.name || 'オリパ'}の詳細ページです。`} />
        <meta property="og:image" content={event?.thumbnail_url || ''} />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:type" content="website" />
        {event?.site?.name && <meta property="og:site_name" content={event.site.name} />}
        <meta property="og:locale" content="ja_JP" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event?.site?.name ? `${event.name || 'オリパ詳細'} | ${event.site.name}` : (event?.name || 'オリパ詳細')} />
        <meta name="twitter:description" content={event?.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 200) : `${event?.name || 'オリパ'}の詳細ページです。`} />
        <meta name="twitter:image" content={event?.thumbnail_url || ''} />
        {event?.hidden_from_list && (
          <meta name="robots" content="noindex, nofollow" />
        )}
      </Helmet>

      {/* メインコンテンツ */}
      <div className="max-w-screen-lg mx-auto pb-72 pt-20 bg-theme-bg">
        {/* Breadcrumb（ヘッダー） */}
        {event?.site && (
          <div className="bg-white border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
            <div className="px-4 py-3">
              <nav className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                <ol className="flex items-center gap-1">
                  <li>
                    <Link to="/" className="hover:text-theme-primary underline underline-offset-2">
                      {event.site.name}
                    </Link>
                  </li>
                  <li className="text-black">/</li>
                  <li className="text-black truncate max-w-[200px]">{event.name}</li>
                </ol>
              </nav>
            </div>
          </div>
        )}

        {/* サムネイル画像 800x525px */}
        <div className="w-full aspect-[800/525] bg-white border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
          <ResponsiveImage
            src={event.thumbnail_url || "/placeholder.svg"}
            variants={event.thumbnail_variant_urls}
            alt={event.name}
            displaySize="full"
            className="w-full h-full object-contain"
          />
        </div>

        {/* 等級別カード一覧 */}
        {event.prizes_with_allocations && event.prizes_with_allocations.length > 0 && (
          <div className="px-4 py-6 bg-gray-50">
            <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black mb-4">ラインナップ</h2>
            <div className="space-y-6">
              {event.prizes_with_allocations.map((prize, prizeIndex) => (
                <PrizeSection
                  key={prize.id || `unassigned-${prizeIndex}`}
                  prize={prize}
                  prizeKey={String(prize.id || `unassigned-${prizeIndex}`)}
                  customModalImages={customPrizeModalImages}
                  productModalImages={productPrizeModalImages}
                />
              ))}
            </div>
          </div>
        )}

        {/* 開催期間 */}
        {(event.starts_at || event.ends_at) && (() => {
          const fmt = (iso: string) =>
            new Date(iso).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
          const periodText = event.starts_at && event.ends_at
            ? `${fmt(event.starts_at)} 〜 ${fmt(event.ends_at)}`
            : event.starts_at
            ? `${fmt(event.starts_at)} 〜`
            : `〜 ${fmt(event.ends_at!)}`;
          return (
            <div className="px-4 py-4 bg-white border-y-[length:var(--theme-border-width)] border-theme">
              <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500 mb-1">開催期間</p>
              <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-800">{periodText}</p>
            </div>
          );
        })()}

        {/* 新規登録ユーザー限定 */}
        {isNewCustomerOnlyEvent && (
          <div className="px-4 py-4 bg-pink-50 border-y-[length:var(--theme-border-width)] border-theme">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-2 bg-pink-400 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight)] text-black shadow-theme-sm">
              新規登録ユーザー限定
            </div>
            <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-800">
              登録から{event.new_customer_only_days}日以内のお客様限定で抽選できます。
            </p>
            {newCustomerEligible && (newCustomerExpiresAtLabel || showNewCustomerDaysRemaining) && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs [font-weight:var(--theme-font-weight-button)] text-pink-700">
                {newCustomerExpiresAtLabel && (
                  <span className="inline-flex items-center rounded-full border border-pink-300 bg-white px-2 py-1">
                    {newCustomerExpiresAtLabel}まで抽選可能
                  </span>
                )}
                {showNewCustomerDaysRemaining && (
                  <span className="inline-flex items-center rounded-full border border-pink-300 bg-white px-2 py-1">
                    残り{newCustomerDaysRemaining}日
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {(paidAmountRequirementEnabled || customerListRestricted) && (
          <div className="px-4 py-4 bg-white border-y-[length:var(--theme-border-width)] border-theme">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm [font-weight:var(--theme-font-weight)] text-gray-800">参加条件</h2>
              {participationStatusKnown && (
                <span className={`text-sm [font-weight:var(--theme-font-weight-button)] ${canDraw ? 'text-green-700' : 'text-gray-600'}`}>
                  {canDraw ? '参加可能' : '参加条件を満たしていません'}
                </span>
              )}
            </div>
            {paidAmountRequirementEnabled && customerListRestricted && (
              <p className="mb-3 text-sm text-gray-600">すべての条件を満たす方が参加できます。</p>
            )}
            <div className="divide-y divide-gray-200">
              {paidAmountRequirementEnabled && (
                <div className={customerListRestricted ? 'pb-3' : ''}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 text-xs leading-[18px] md:text-sm md:leading-5 [font-weight:var(--theme-font-weight-button)] text-gray-700">
                      {paidAmountRequirementScopeLabel}{formatAmount(event.paid_amount_requirement_minimum)}pt以上
                    </p>
                    {event.paid_amount_requirement_eligible != null && (
                      <span className={`shrink-0 text-sm [font-weight:var(--theme-font-weight-button)] ${event.paid_amount_requirement_eligible ? 'text-green-700' : 'text-red-600'}`}>
                        {event.paid_amount_requirement_eligible ? '達成' : '未達'}
                      </span>
                    )}
                  </div>
                  {event.paid_amount_requirement_eligible === false && (
                    <p className="mt-1 text-sm text-gray-600">
                      現在のチャージ実績 {formatAmount(event.paid_amount_requirement_current_amount)}pt
                    </p>
                  )}
                </div>
              )}
              {customerListRestricted && (
                <div className={paidAmountRequirementEnabled ? 'pt-3' : ''}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 whitespace-pre-line break-words text-xs leading-[18px] md:text-sm md:leading-5 [font-weight:var(--theme-font-weight-button)] text-gray-700">
                      {customerListConditionText}
                    </p>
                    {event.customer_list_eligible != null && (
                      <span className={`shrink-0 text-sm [font-weight:var(--theme-font-weight-button)] ${event.customer_list_eligible ? 'text-green-700' : 'text-red-600'}`}>
                        {event.customer_list_eligible ? '対象' : '対象外'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {!participationStatusKnown && (
              <p className="text-xs text-gray-500 mt-2">
                {currentUser ? '参加条件を確認しています。' : 'ログイン後に参加条件の達成状況を確認できます。'}
              </p>
            )}
          </div>
        )}

        {isTotalLimitEvent && (
          <div className="px-4 py-4 bg-blue-50 border-y-[length:var(--theme-border-width)] border-theme">
            <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500 mb-1">通算抽選回数制限</p>
            <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-800">
              お一人様、通算{totalPlayLimit}回まで参加できます。
              {currentUser && totalPlaysRemaining != null && ` 残り${totalPlaysRemaining}回です。`}
            </p>
          </div>
        )}

        {/* イベント説明 */}
        {event.description && (
          <div className="px-4 py-6 bg-white border-y-[length:var(--theme-border-width)] border-theme">
            <div
              className="prose prose-xs max-w-none text-gray-700 text-xs leading-relaxed [font-weight:var(--theme-font-weight-button)] [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_h2]:text-sm [&_h2]:[font-weight:var(--theme-font-weight)] [&_h2]:mt-3 [&_h2]:mb-1.5"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>
        )}

        {/* Breadcrumb（ページ下部） */}
        <div className="px-4 py-4 bg-gray-50">
          <nav className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
            <ol className="flex items-center gap-1 flex-wrap">
              {event.site && (
                <>
                  <li>
                    <Link to="/" className="hover:text-theme-primary underline underline-offset-2">
                      {event.site.name}
                    </Link>
                  </li>
                  <li className="text-black">/</li>
                </>
              )}
              <li className="text-black">{event.name}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* 固定ガチャボタン */}
      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-screen-lg border-t-[length:var(--theme-border-width)] border-t-[color:var(--theme-border-color)] bg-white px-4 pt-3 pb-6 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm [font-weight:var(--theme-font-weight)] text-black truncate">
            {event.name}
          </span>
          {!hideRemainingStock && (
            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 whitespace-nowrap ml-2">
              残り {availableStock.toLocaleString()} 口
            </span>
          )}
        </div>

        {/* プログレスバー（途中終了時は非表示） */}
        {!hideRemainingStock && (
          <div className="mb-3">
            <div className="h-3 rounded-theme bg-gray-200 border-theme overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${progressColor}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {(isNewCustomerOnlyEvent || isDailyLimitEvent || isTotalLimitEvent || paidAmountRequirementEnabled || customerListRestricted) && (
          <div className="mb-2 flex flex-wrap items-center justify-center gap-1.5 text-[11px] [font-weight:var(--theme-font-weight-button)]">
            {isNewCustomerOnlyEvent && newCustomerEligible && (
              <span className="inline-flex items-center rounded-full border border-pink-300 bg-pink-50 px-2 py-1 text-pink-700">
                {newCustomerExpiresAtLabel
                  ? `新規限定: ${newCustomerExpiresAtLabel}まで`
                  : `新規限定: 登録から${event.new_customer_only_days}日以内`}
              </span>
            )}
            {isDailyLimitEvent && (
              <span className="inline-flex items-center rounded-full border border-yellow-300 bg-yellow-50 px-2 py-1 text-yellow-700">
                1日{dailyPlayLimit}回限定
              </span>
            )}
            {isTotalLimitEvent && (
              <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2 py-1 text-blue-700">
                通算{totalPlayLimit}回限定{currentUser && totalPlaysRemaining != null ? `・残り${totalPlaysRemaining}回` : ''}
              </span>
            )}
            {(paidAmountRequirementEnabled || customerListRestricted) && (
              <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2 py-1 text-blue-700">
                参加条件あり
              </span>
            )}
          </div>
        )}

        {!canDraw ? (
          // 抽選不可時の表示
          <div className="py-2 text-center">
            {isNewCustomerOnlyEvent && !newCustomerEligible ? (
              <div className="space-y-1">
                <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-700">
                  このオリパは新規登録から{event.new_customer_only_days}日以内のお客様限定です。
                </p>
                <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                  対象期間を過ぎているため抽選できません。
                </p>
              </div>
            ) : totalLimitReached ? (
              <div className="space-y-1">
                <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-700">
                  このオリパの通算抽選回数上限に達しました
                </p>
              </div>
            ) : dailyLimitReached ? (
              // ログボガチャ: 完了表示（ミニマル）
              <div className="space-y-1">
                <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-600">
                  ✅ 本日のガチャは完了しました
                </p>
                {newCustomerExpiresBeforeDailyReset && newCustomerExpiresAtLabel ? (
                  <p className="text-xs [font-weight:var(--theme-font-weight)] text-pink-700" aria-live="polite">
                    新規限定期間は {newCustomerExpiresAtLabel} までです
                  </p>
                ) : (
                  <p className="[font-weight:var(--theme-font-weight)] text-gray-500" role="timer" aria-live="polite" aria-label={`あと ${dailyCountdown || '計算中'} でリセット`}>
                    <span className="text-xs">あと</span>{' '}
                    <span className="text-xl font-mono tabular-nums text-gray-800">{dailyCountdown || '--:--:--'}</span>{' '}
                    <span className="text-xs">でリセット</span>
                  </p>
                )}
              </div>
            ) : isLegacyEvent ? (
              <p className="text-lg [font-weight:var(--theme-font-weight)] text-gray-500">
                現在ご利用いただけません
              </p>
            ) : !isActive ? (
              <p className="text-lg [font-weight:var(--theme-font-weight)] text-gray-500">
                {event.status === 'finished' || event.status === 'ended'
                  ? '終了しました'
                  : event.status === 'draft' || event.status === 'ready' || event.status === 'published'
                  ? '近日開催予定！'
                  : '現在利用できません'}
              </p>
            ) : isBeforeStart ? (
              <p className="text-lg [font-weight:var(--theme-font-weight)] text-gray-500">
                開催前です
              </p>
            ) : isAfterEnd ? (
              <p className="text-lg [font-weight:var(--theme-font-weight)] text-gray-500">
                終了しました
              </p>
            ) : paidAmountRequirementBlocked ? (
              <div className="space-y-1">
                <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-600">
                  チャージ実績の条件を満たしていません
                </p>
                <p className="text-xs text-gray-500">
                  {paidAmountRequirementScopeLabel}
                  {formatAmount(event.paid_amount_requirement_minimum)}pt以上のお客様限定です
                  （現在 {formatAmount(event.paid_amount_requirement_current_amount)}pt）
                </p>
              </div>
            ) : customerListBlocked ? (
              <p className="text-sm [font-weight:var(--theme-font-weight)] text-gray-600">
                このイベントの参加対象ではありません
              </p>
            ) : (
              <p className="text-lg [font-weight:var(--theme-font-weight)] text-gray-500">終了しました</p>
            )}
          </div>
        ) : isDailyLimitEvent ? (
          // ログボガチャ: 専用ボタン（1回引きのみ、連打防止付き）
          <div>
            <ThemedButton
              variant="primary"
              className="w-full p-3 text-sm"
              disabled={isAnimationPlaying}
              onClick={(e) => event && handleGachaClick(e, 'single', siteId.toString(), eventId, gachaCost, 0)}
            >
              🎁 今日のガチャを引く！（{gachaCost}pt{dailyPlaysRemaining != null ? ` / 残り${dailyPlaysRemaining}回` : ''}）
            </ThemedButton>
            <p className="text-xs text-gray-400 text-center mt-1">毎日0時リセット</p>
          </div>
        ) : (
          // 通常ガチャボタン
          <div>
            <div className="grid grid-cols-3 gap-2">
              <ThemedButton
                variant="outline"
                className="w-full p-3 text-sm"
                disabled={isTotalLimitEvent && totalPlaysRemaining != null && totalPlaysRemaining < 1}
                onClick={(e) => event && handleGachaClick(e, 'single', siteId.toString(), eventId, gachaCost, 0)}
              >
                1回
              </ThemedButton>
              <ThemedButton
                variant="primary"
                className="w-full p-3 text-sm"
                disabled={isTotalLimitEvent && totalPlaysRemaining != null && totalPlaysRemaining < 10}
                onClick={(e) => event && handleGachaClick(e, 'multi10', siteId.toString(), eventId, gachaCost, 0)}
              >
                10連
              </ThemedButton>
              <ThemedButton
                variant="secondary"
                colorClass="bg-pink-400"
                className="w-full p-3 text-sm text-black"
                disabled={isTotalLimitEvent && totalPlaysRemaining != null && totalPlaysRemaining < 100}
                onClick={(e) => event && handleGachaClick(e, 'multi100', siteId.toString(), eventId, gachaCost, 0)}
              >
                100連
              </ThemedButton>
            </div>
          </div>
        )}
      </div>

      {/* 確認ダイアログ */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ガチャを実行しますか？</DialogTitle>
            <DialogDescription>
              {pendingGacha?.cost} ポイント消費して{pendingGacha?.count}回引きます
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button onClick={executeGacha}>
              実行する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 未ログインダイアログ */}
      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ログインしてください</DialogTitle>
            <DialogDescription>
              ガチャを回すためにはログインが必要です。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsLoginDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button asChild>
              <Link to="/login">ログインページへ</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ポイント不足ダイアログ */}
      <Dialog open={isPointDialogOpen} onOpenChange={setIsPointDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ポイントが不足しています</DialogTitle>
            <DialogDescription>
              ガチャを回すためにはポイントが必要です。
              <br />
              ポイントはポイント購入ページでチャージできます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsPointDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button asChild>
              <Link to="/point">オリパポイントを購入</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 抽選アニメーション */}
      <LotteryAnimation
        animationUrl={currentAnimationUrl || event.lottery_animation_url}
        isPlaying={isAnimationPlaying}
        onAnimationEnd={onAnimationEnd}
        onSkipAll={skipAllAnimations}
        gachaCount={pendingGacha?.count || 1}
        currentIndex={currentAnimationIndex}
        totalCount={drawResults.length}
      />

      {/* 電話番号認証ダイアログ */}
      <PhoneVerificationDialog
        isOpen={isPhoneVerificationRequired}
        onClose={() => setIsPhoneVerificationRequired(false)}
        onSuccess={() => {
          setIsPhoneVerificationRequired(false);
        }}
      />
    </>
  );
};

// 等級セクションコンポーネント
const PrizeSection: React.FC<{
  prize: PrizeWithAllocations;
  prizeKey: string;
  customModalImages: ModalImage[];
  productModalImages: ModalImage[];
}> = ({ prize, prizeKey, customModalImages, productModalImages }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (prize.allocations.length === 0) return null;

  const displayThumbnailUrls = prize.display_thumbnail_urls || [];
  const displayThumbnailVariants = prize.display_thumbnail_variant_urls || [];
  const hasDisplayThumbnails =
    prize.display_thumbnail_mode === "custom_thumbnails" && displayThumbnailUrls.length > 0;

  const sectionModalImages = buildPrizeModalImages(prize, prizeKey);
  const modalImages = hasDisplayThumbnails ? customModalImages : productModalImages;
  const selectedImage = selectedIndex !== null ? modalImages[selectedIndex] : null;

  const closeModal = () => setSelectedIndex(null);

  const showPrev = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + modalImages.length) % modalImages.length);
    }
  };

  const showNext = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % modalImages.length);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="px-3 py-1.5 bg-black text-white text-sm [font-weight:var(--theme-font-weight)] rounded-theme border-theme shadow-theme-sm">
          {prize.name}
        </span>
        <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
          {prize.allocations.length}点
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {hasDisplayThumbnails ? (
          displayThumbnailUrls.map((thumbnailUrl, index) => (
            <button
              type="button"
              key={`${thumbnailUrl}-${index}`}
              className="aspect-[63/88] bg-white rounded-theme overflow-hidden border-theme shadow-theme-sm cursor-pointer hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              onClick={() => {
                const modalIndex = modalImages.findIndex((image) => image.key === sectionModalImages[index]?.key);
                setSelectedIndex(modalIndex >= 0 ? modalIndex : index);
              }}
              aria-label={`${prize.name} 掲載デザイン ${index + 1}を拡大表示`}
            >
              <ResponsiveImage
                src={thumbnailUrl}
                variants={displayThumbnailVariants[index]}
                alt={`${prize.name} 掲載デザイン ${index + 1}`}
                displaySize="card"
                className="w-full h-full object-cover"
              />
            </button>
          ))
        ) : (
          prize.allocations.map((allocation, index) => (
            <button
              type="button"
              key={allocation.id}
              className="aspect-[63/88] bg-white rounded-theme overflow-hidden border-theme shadow-theme-sm cursor-pointer hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              onClick={() => {
                const modalIndex = modalImages.findIndex((image) => image.key === `allocation-${allocation.id}`);
                setSelectedIndex(modalIndex >= 0 ? modalIndex : index);
              }}
              aria-label={`${allocation.product_name || "商品"}の詳細を表示`}
            >
              {allocation.image_url ? (
                <ResponsiveImage
                  src={allocation.image_url}
                  variants={allocation.image_variant_urls}
                  alt={allocation.product_name || ""}
                  displaySize="card"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <ImageOff className="w-6 h-6 text-gray-400" />
                </div>
              )}
            </button>
          ))
        )}
      </div>

      <Dialog open={selectedImage !== null} onOpenChange={(open) => !open && closeModal()}>
        {selectedImage && (
          <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            onPointerDownOutside={closeModal}
            className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-1rem)] w-fit max-w-[calc(100vw-1rem)] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg bg-white p-2 ![box-shadow:0_24px_80px_rgba(0,0,0,0.35)!important] focus:outline-none sm:p-3 md:p-4"
          >
            <DialogTitle className="sr-only">{selectedImage.alt}の拡大画像</DialogTitle>

            <DialogClose asChild>
              <button
                type="button"
                className="absolute right-3 top-3 z-20 flex h-11 w-11 appearance-none items-center justify-center rounded-full !bg-black/70 p-0 !text-white ![box-shadow:none!important] ![filter:none!important] transition-colors hover:!bg-black/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black [&_svg]:![filter:none!important]"
                aria-label="閉じる"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogClose>

            {modalImages.length > 1 && (
              <button
                type="button"
                className="absolute left-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 appearance-none items-center justify-center rounded-full !bg-black/70 p-0 !text-white ![box-shadow:none!important] ![filter:none!important] transition-colors hover:!bg-black/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black md:left-4 md:h-14 md:w-14 [&_svg]:![filter:none!important]"
                onClick={showPrev}
                aria-label="前の画像"
              >
                <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
              </button>
            )}

            {modalImages.length > 1 && (
              <button
                type="button"
                className="absolute right-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 appearance-none items-center justify-center rounded-full !bg-black/70 p-0 !text-white ![box-shadow:none!important] ![filter:none!important] transition-colors hover:!bg-black/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black md:right-4 md:h-14 md:w-14 [&_svg]:![filter:none!important]"
                onClick={showNext}
                aria-label="次の画像"
              >
                <ChevronRight className="h-6 w-6 md:h-7 md:w-7" />
              </button>
            )}

            <div className="relative flex max-h-[calc(100dvh-2rem)] w-[min(82vw,620px)] items-center justify-center">
              {selectedImage.url ? (
                <ResponsiveImage
                  src={selectedImage.url}
                  variants={selectedImage.variants}
                  alt={selectedImage.alt}
                  displaySize="full"
                  className="max-h-[calc(100dvh-2rem)] w-full object-contain"
                />
              ) : (
                <div className="flex aspect-[63/88] max-h-[calc(100dvh-2rem)] w-full items-center justify-center rounded-theme bg-gray-100">
                  <ImageOff className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
          </DialogPortal>
        )}
      </Dialog>
    </div>
  );
};

export default EventDetailsPage;
